// Developer: Ankit Gupta
import React, { useEffect } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Tabs,
  Select,
  Space,
  Typography,
  Spin,
  Alert,
  Breadcrumb,
  theme,
} from 'antd';
import {
  InfoCircleOutlined,
  SettingOutlined,
  TeamOutlined,
  CloudServerOutlined,
  DeploymentUnitOutlined,
  HomeOutlined,
  CloudDownloadOutlined,
  ApiOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useProject, useProjects } from '@/hooks/useProject';
import { useActiveScope } from '@/hooks/useActiveScope';
import { ProjectStatusBadge } from '@/components/common/StatusBadge';
import { CAD_SOURCE_SYSTEM } from '@/constants/sourceSystems';
import { useT } from '@/i18n';

const { Title, Text } = Typography;

// ─── Tab definitions ──────────────────────────────────────────────────────────

interface TabDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: (projectId: string) => string;
  // When present, the tab is shown only if this predicate returns true for the
  // current project (e.g. CAD-only tabs).
  visibleFor?: (sourceSystem: string | undefined) => boolean;
}

const isCad = (sourceSystem: string | undefined) => sourceSystem === CAD_SOURCE_SYSTEM;

const TABS: TabDef[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: <InfoCircleOutlined />,
    path: (id) => `/projects/${id}`,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: <SettingOutlined />,
    path: (id) => `/projects/${id}/settings`,
  },
  {
    key: 'members',
    label: 'Team Members',
    icon: <TeamOutlined />,
    path: (id) => `/projects/${id}/members`,
  },
  {
    key: 'environments',
    label: 'Environments',
    icon: <CloudServerOutlined />,
    path: (id) => `/projects/${id}/environments`,
  },
  {
    key: 'entity-libraries',
    label: 'Entity Libraries',
    icon: <DeploymentUnitOutlined />,
    path: (id) => `/projects/${id}/entity-libraries`,
  },
  {
    key: 'templates',
    label: 'Templates',
    icon: <FileExcelOutlined />,
    path: (id) => `/projects/${id}/templates`,
  },
  {
    key: 'fusion-extracts',
    label: 'Fusion Extracts',
    icon: <CloudDownloadOutlined />,
    path: (id) => `/projects/${id}/fusion-extracts`,
  },
  {
    key: 'source-mapping',
    label: 'Source Mapping',
    icon: <FileExcelOutlined />,
    path: (id) => `/projects/${id}/source-mapping`,
    // CAD projects ingest drawings via API sources, not Excel workbooks.
    visibleFor: (ss) => !isCad(ss),
  },
  {
    key: 'api-sources',
    label: 'API Sources',
    icon: <ApiOutlined />,
    path: (id) => `/projects/${id}/api-sources`,
    visibleFor: isCad,
  },
  // CAD Inbox is no longer a project tab - drawing ingestion now lives under
  // Migrate Data > Submit Run (the page swaps to the CAD inbox for CAD projects).
];

// ─────────────────────────────────────────────────────────────────────────────

export function ProjectLayout() {
  const { t } = useT('shell');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { token: designToken } = theme.useToken();

  const { data: project, isLoading, error } = useProject(id);
  const { data: projects = [] } = useProjects();
  const { selectProject, activeProjectId } = useActiveScope();

  // Determine active tab from the current URL segment
  const activeTab = deriveActiveTab(location.pathname);

  // Tabs gated by source system (e.g. API Sources / CAD Inbox are CAD-only).
  const visibleTabs = TABS.filter(
    (tab) => !tab.visibleFor || tab.visibleFor(project?.source_system),
  ).map((tab) => ({ ...tab, label: t(`projectTabs.${tab.key}`, { defaultValue: tab.label }) }));

  // Synchronize global active project scope when viewing this project
  useEffect(() => {
    if (project?.id && project.id !== activeProjectId) {
      selectProject(project.id);
    }
  }, [project?.id, activeProjectId, selectProject]);

  function handleTabChange(tabKey: string) {
    const tabDef = TABS.find((tab) => tab.key === tabKey);
    if (tabDef && id) navigate(tabDef.path(id));
  }

  function handleProjectChange(newId: number) {
    selectProject(newId);
    navigate(`/projects/${newId}`);
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 64 }}>
        <Spin size="large" />
        <span style={{ color: 'var(--dp-ink-dim)' }}>{t('project.loading')}</span>
      </div>
    );
  }

  if (error || !project) {
    return (
      <Alert
        type="error"
        message={t('project.loadFailed')}
        description={(error as Error)?.message ?? t('project.notFound')}
        showIcon
      />
    );
  }

  const currentTabDef = visibleTabs.find((tab) => tab.key === activeTab);

  return (
    <div>
      {/* ── Breadcrumb ── */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <a
                onClick={() => navigate('/')}
                style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--dp-ink-dim)' }}
              >
                <HomeOutlined />
              </a>
            ),
          },
          {
            title: (
              <a onClick={() => navigate('/projects')} style={{ color: 'var(--dp-ink-dim)' }}>
                {t('nav.projects')}
              </a>
            ),
          },
          {
            title: (
              <a
                onClick={() => navigate(`/projects/${project.id}`)}
                style={{
                  fontWeight: currentTabDef?.key === 'overview' ? 600 : 400,
                  color: currentTabDef?.key === 'overview' ? 'var(--dp-ink)' : 'var(--dp-ink-dim)',
                }}
              >
                {project.project_code}
              </a>
            ),
          },
          ...(currentTabDef && currentTabDef.key !== 'overview'
            ? [
                {
                  title: (
                    <span style={{ fontWeight: 600, color: 'var(--dp-ink)' }}>
                      {currentTabDef.label}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />

      {/* ── Project header ── */}
      <div
        style={{
          background: designToken.colorBgContainer,
          borderRadius: designToken.borderRadiusLG,
          padding: '20px 24px 0',
          marginBottom: 0,
          border: `1px solid ${designToken.colorBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 14,
          }}
        >
          <div>
            <Space align="center" size={10} wrap>
              <Title level={4} style={{ margin: 0 }}>
                {project.project_name}
              </Title>
              <ProjectStatusBadge status={project.status} />
            </Space>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">
                {project.project_code} · {project.customer_name} · {t('project.source', { source: project.source_system })}
              </Text>
            </div>
          </div>

          {/* Project switcher */}
          <Select
            value={project.id}
            style={{ width: 240, minWidth: 200 }}
            onChange={handleProjectChange}
            showSearch
            optionFilterProp="label"
            placeholder={t('project.switch')}
            options={projects.map((p) => ({
              value: p.id,
              label: `${p.project_code} – ${p.project_name}`,
            }))}
          />
        </div>

        {/* ── Tabs ── */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarStyle={{ marginBottom: 0 }}
          items={visibleTabs.map((tab) => ({
            key: tab.key,
            label: (
              <Space size={6}>
                {tab.icon}
                {tab.label}
              </Space>
            ),
          }))}
        />
      </div>

      {/* ── Tab content ── */}
      <div
        style={{
          background: designToken.colorBgContainer,
          borderRadius: `0 0 ${designToken.borderRadiusLG}px ${designToken.borderRadiusLG}px`,
          border: `1px solid ${designToken.colorBorder}`,
          borderTop: 'none',
          padding: 24,
          minHeight: 400,
        }}
      >
        <Outlet />
      </div>
    </div>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function deriveActiveTab(pathname: string): string {
  if (pathname.endsWith('/settings')) return 'settings';
  if (pathname.endsWith('/members')) return 'members';
  if (pathname.endsWith('/environments')) return 'environments';
  // Legacy paths (entities / validations / transforms) now live under Entity Libraries.
  if (pathname.endsWith('/templates')) return 'templates';
  if (pathname.endsWith('/entity-libraries')) return 'entity-libraries';
  if (pathname.endsWith('/entities')) return 'entity-libraries';
  if (pathname.endsWith('/validations')) return 'entity-libraries';
  if (pathname.endsWith('/transforms')) return 'entity-libraries';
  if (pathname.endsWith('/fusion-extracts')) return 'fusion-extracts';
  if (pathname.endsWith('/source-mapping')) return 'source-mapping';
  if (pathname.endsWith('/api-sources')) return 'api-sources';
  if (pathname.endsWith('/cad-inbox')) return 'cad-inbox';
  if (pathname.endsWith('/runs')) return 'runs';
  return 'overview';
}

export default ProjectLayout;
